import { useState } from 'react';
import type { FC, MouseEvent } from 'react';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import type { ScreenOnTime } from '@/entities/screen';
import {
  DEFAULT_TIMEFRAME,
  END_HOURS,
  MINUTES,
  START_HOURS,
} from './constants';
import type { TimeBoundary, TimePart, TimePickerProps } from './types';

const cloneTimeframe = (timeframe: ScreenOnTime): ScreenOnTime => ({
  from: { ...timeframe.from },
  to: { ...timeframe.to },
});

const formatTime = ({ hours, minutes }: ScreenOnTime[TimeBoundary]) =>
  `${hours}:${minutes}`;

const TimePicker: FC<TimePickerProps> = ({
  timeframe,
  onTimeRangeChange,
  error = false,
  helperText,
  label = 'Select Time Range',
  disabled = false,
  placeholder = 'HH:mm',
  fullWidth = false,
  required = true,
  className,
  sx,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeBoundary, setActiveBoundary] = useState<TimeBoundary>('from');
  const [draftTimeframe, setDraftTimeframe] = useState<ScreenOnTime>(
    cloneTimeframe(timeframe ?? DEFAULT_TIMEFRAME)
  );

  const isPickerOpen = Boolean(anchorEl);
  const activeTime = draftTimeframe[activeBoundary];
  const hourOptions = activeBoundary === 'from' ? START_HOURS : END_HOURS;

  const handleOpen =
    (boundary: TimeBoundary) => (event: MouseEvent<HTMLElement>) => {
      if (disabled) return;

      setActiveBoundary(boundary);
      setDraftTimeframe(cloneTimeframe(timeframe ?? DEFAULT_TIMEFRAME));
      setAnchorEl(event.currentTarget);
    };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTimePartChange = (part: TimePart, value: string) => {
    setDraftTimeframe(current => {
      const nextTime = {
        ...current[activeBoundary],
        [part]: value,
      };

      if (activeBoundary === 'to' && part === 'hours' && value === '24') {
        nextTime.minutes = '00';
      }

      return {
        ...current,
        [activeBoundary]: nextTime,
      };
    });
  };

  const handleApply = () => {
    onTimeRangeChange(cloneTimeframe(draftTimeframe));
    handleClose();
  };

  return (
    <Box className={className} sx={sx}>
      {label && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}

      <Grid container spacing={2}>
        {(
          [
            ['from', 'Start Time'],
            ['to', 'End Time'],
          ] as const
        ).map(([boundary, fieldLabel]) => (
          <Grid item xs={12} sm={6} key={boundary}>
            <TextField
              fullWidth={fullWidth}
              label={fieldLabel}
              placeholder={placeholder}
              value={timeframe ? formatTime(timeframe[boundary]) : ''}
              onClick={handleOpen(boundary)}
              error={error}
              disabled={disabled}
              required={required}
              inputProps={{
                readOnly: true,
                'aria-label': fieldLabel,
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    edge="end"
                    onClick={handleOpen(boundary)}
                    disabled={disabled}
                    aria-label={`Choose ${fieldLabel.toLowerCase()}`}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    <AccessTimeIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: disabled ? 'default' : 'pointer',
                '& .MuiInputBase-input': {
                  cursor: disabled ? 'default' : 'pointer',
                },
              }}
            />
          </Grid>
        ))}
      </Grid>

      <FormHelperText error={error} sx={{ m: 0, mt: 0.5 }}>
        {helperText}
      </FormHelperText>

      <Popover
        open={isPickerOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: { xs: 280, sm: 340 },
              borderRadius: 2,
              boxShadow: theme.shadows[8],
            },
          },
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <AccessTimeIcon color="primary" />
            Select {activeBoundary === 'from' ? 'Start Time' : 'End Time'}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Hour"
                value={activeTime.hours}
                onChange={event =>
                  handleTimePartChange('hours', event.target.value)
                }
                inputProps={{
                  'aria-label': `${
                    activeBoundary === 'from' ? 'Start' : 'End'
                  } Time hour`,
                }}
              >
                {hourOptions.map(hour => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Minute"
                value={activeTime.minutes}
                disabled={activeBoundary === 'to' && activeTime.hours === '24'}
                onChange={event =>
                  handleTimePartChange('minutes', event.target.value)
                }
                inputProps={{
                  'aria-label': `${
                    activeBoundary === 'from' ? 'Start' : 'End'
                  } Time minute`,
                }}
              >
                {MINUTES.map(minute => (
                  <MenuItem key={minute} value={minute}>
                    {minute}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Box
            sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}
          >
            <Button variant="outlined" onClick={handleClose} size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApply}
              startIcon={<CheckIcon />}
              size="small"
            >
              Apply
            </Button>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export default TimePicker;
